import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faRotateLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { useImageCrop } from '@/lib/image-crop/use-image-crop';
import type { ImageLoader } from '@/lib/image-crop/load-image';
import { Button } from '@/components/ui/button';
import { Modal } from './Modal';

/**
 * PP-1 — the staff crop editor (PP spec §5.2). A shell over the headless
 * `useImageCrop` hook using the tenant theme tokens and the shared `Modal`.
 *
 * Interaction: drag / pan (pointer capture, so touch and mouse behave the
 * same), pinch, wheel, keyboard (arrows, + / −, Home), a labelled slider and
 * 44 px buttons; a live circular preview rendered from the SAME transform;
 * nothing is uploaded until "Save photo" — Cancel, Escape and the Close
 * button discard. The backdrop never closes it (work in progress). The parent
 * owns the upload and closes the editor by clearing `file`; a rejected
 * `onConfirm` keeps the editor open and announces the problem.
 */
export interface CropPhotoEditorProps {
  file: File | null;
  open: boolean;
  onCancel: () => void;
  /** Receives the exported 512 px JPEG. Resolve `true` (or void) on success; reject or resolve `false` to stay open. */
  onConfirm: (cropped: File) => Promise<boolean | void> | boolean | void;
  /** External busy state (the upload in flight). */
  saving?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  title?: string;
  /** Test seam — replaces the object-URL loader. */
  loadImage?: ImageLoader;
}

// PP-1.1: `shrink-0` — the zoom row is a flex row that also holds the slider;
// without it the browser shrinks these buttons to their icon width (24 px was
// measured in a real Chrome at every viewport size) and the 44 px touch target
// exists only in the class list. The slider takes the remaining width instead.
const CONTROL =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-muted disabled:opacity-50 disabled:hover:bg-card motion-reduce:transition-none';

export function CropPhotoEditor({
  file,
  open,
  onCancel,
  onConfirm,
  saving = false,
  firstName,
  lastName,
  title = 'Adjust your photo',
  loadImage,
}: CropPhotoEditorProps) {
  const isOpen = open && !!file;
  const crop = useImageCrop(isOpen ? file : null, { loadImage });
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setProblem(null);
      setExporting(false);
    }
  }, [isOpen]);

  const busy = saving || exporting;
  const ready = crop.status === 'ready' && !busy;

  const cancel = () => {
    if (busy) return;
    onCancel();
  };

  const save = async () => {
    if (!ready) return;
    setProblem(null);
    setExporting(true);
    try {
      const cropped = await crop.exportSquare();
      const ok = await onConfirm(cropped);
      if (ok === false) setProblem('The photo could not be saved. Try again.');
    } catch (err) {
      setProblem(
        err instanceof Error && err.message === 'CANVAS_UNAVAILABLE'
          ? 'Your browser could not prepare the photo. Try a different browser.'
          : 'The photo could not be saved. Try again.',
      );
    } finally {
      setExporting(false);
    }
  };

  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const { ref: setViewportEl, ...viewportProps } = crop.viewportProps;

  return (
    <Modal
      open={isOpen}
      onClose={cancel}
      title={title}
      size="md"
      disableBackdropClose
      initialFocusRef={viewportRef}
    >
      <div
        className="flex flex-col items-center gap-5 sm:flex-row sm:items-start sm:gap-8"
        data-testid="crop-photo-editor"
      >
        <div className="w-full max-w-full sm:max-w-[20rem]">
          {/* Viewport */}
          <div
            {...viewportProps}
            ref={(el) => {
              viewportRef.current = el;
              setViewportEl(el);
            }}
            data-testid="crop-viewport"
            className={cn(
              'relative aspect-square w-full select-none overflow-hidden rounded-lg bg-muted',
              'touch-none cursor-grab active:cursor-grabbing',
              'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            {crop.status === 'loading' && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                <FontAwesomeIcon icon={faSpinner} spin className="h-6 w-6" />
              </div>
            )}
            {crop.status === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-destructive">
                That image could not be opened. Choose a JPEG, PNG or WEBP photo.
              </div>
            )}
            {crop.imageSrc && (
              <img
                src={crop.imageSrc}
                alt=""
                draggable={false}
                style={crop.imageStyle}
                data-testid="crop-image"
                className="pointer-events-none transition-transform duration-75 ease-out motion-reduce:transition-none"
              />
            )}
            {/* Circular mask — everything outside the circle is dimmed. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] ring-2 ring-white/70"
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Drag to reposition. Scroll, pinch or use the slider to zoom.
          </p>

          {/* Zoom controls */}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              className={CONTROL}
              aria-label="Zoom out"
              disabled={!ready}
              onClick={() => crop.zoomBy(1 / 1.1)}
            >
              <FontAwesomeIcon icon={faMinus} className="h-3.5 w-3.5" />
            </button>
            <input
              type="range"
              aria-label="Zoom"
              aria-valuetext={`${crop.zoomPercent}%`}
              min={crop.minScale}
              max={crop.maxScale}
              step={(crop.maxScale - crop.minScale) / 200 || 0.01}
              value={crop.scale}
              disabled={!ready}
              onChange={(e) => crop.setScale(Number(e.target.value))}
              className="h-11 min-w-0 flex-1 accent-primary"
            />
            <button
              type="button"
              className={CONTROL}
              aria-label="Zoom in"
              disabled={!ready}
              onClick={() => crop.zoomBy(1.1)}
            >
              <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={cn(CONTROL, 'w-auto gap-1.5 px-3 text-xs')}
              disabled={!ready}
              onClick={() => crop.reset()}
            >
              <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
              Reset
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="relative h-24 w-24 overflow-hidden rounded-full border border-border bg-muted"
            data-testid="crop-preview"
            aria-hidden="true"
          >
            {crop.imageSrc && (
              <img
                src={crop.imageSrc}
                alt=""
                draggable={false}
                style={crop.previewStyle(96)}
                className="pointer-events-none"
              />
            )}
          </div>
          <span className="text-xs text-muted-foreground">Preview{name ? ` · ${name}` : ''}</span>
        </div>
      </div>

      {/* Announcements for assistive technology */}
      <div aria-live="polite" className="sr-only" data-testid="crop-live">
        {problem ?? (crop.status === 'ready' ? `Zoom ${crop.zoomPercent}%` : '')}
      </div>
      {problem && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {problem}
        </p>
      )}

      {/* Footer */}
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="secondary"
          className="min-h-11 sm:min-h-9"
          onClick={cancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="min-h-11 sm:min-h-9"
          onClick={() => void save()}
          disabled={!ready}
        >
          {busy ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-3.5 w-3.5" />
              Saving…
            </>
          ) : (
            'Save photo'
          )}
        </Button>
      </div>
    </Modal>
  );
}
